import * as React from 'react';
import './kucoin-history.scss';
import { Col, Empty, Modal, Popover, Row, Table as AntdTable, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'jalali-moment';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Button, buttonType } from 'components/button/button';
import { Input } from 'components/input/input';
import { Dropdown } from 'components/dropdown/dropdown';
import { CopyableText, UserLink } from 'components';
import { PaginationList } from 'core/domain/commons/common';
import { KuCoinHistory, KuCoinHistoryType } from 'core/domain/kucoin-history/kucoin-history';
import { getAllKuCoinHistory } from 'core/repositories/kucoin-history';
import { digitSperator } from 'core/utils/decimalExtentions';
import { downloadCSV } from 'core/utils/export-to-file';
import { getIconPath } from 'core/utils/getIconPath';

type SortOrder = 'ascend' | 'descend';

const DEFAULT_PAGE_SIZE = 50;

const HISTORY_TYPE_ITEMS = [
    { id: KuCoinHistoryType.Deposit, title: 'گزارش واریز' },
    { id: KuCoinHistoryType.Withdrawal, title: 'گزارش برداشت' },
];

const STATUS_ITEMS = [
    { id: '', title: 'همه وضعیت‌ها' },
    { id: 'Processing', title: 'در حال پردازش' },
    { id: 'Success', title: 'موفق' },
    { id: 'Failure', title: 'ناموفق' },
];
const WITHDRAWAL_STATUS_ITEMS = [
    ...STATUS_ITEMS,
    { id: 'WalletProcessing', title: 'در حال پردازش کیف پول' },
];
const DEPOSIT_STATUS_ITEMS = [
    ...STATUS_ITEMS,
    { id: 'PreSuccess', title: 'در انتظار تأیید نهایی' },
    { id: 'WaitingComplianceVerification', title: 'در انتظار بررسی انطباق' },
    { id: 'ComplianceVerificationFailed', title: 'رد بررسی انطباق' },
    { id: 'RollingBack', title: 'در حال بازگشت' },
    { id: 'RolledBack', title: 'برگشت خورده' },
    { id: 'WaitingRiskManagement', title: 'در انتظار بررسی ریسک' },
    { id: 'RiskManagementRejected', title: 'رد بررسی ریسک' },
];

/** نوع گزارش و مرتب‌سازی همیشه داخل کوئری‌اند تا هر صفحه با همان فیلترها خوانده شود. */
const buildQuery = (search: string, historyType: KuCoinHistoryType, sort: SortOrder, status: string) => {
    // بک‌اند: ۱ = قدیمی‌ترین اول، ۲ = جدیدترین اول
    let query = `?type=${historyType}&sortByCreatedOn=${sort === 'ascend' ? 1 : 2}`;
    if (status) query += `&status=${encodeURIComponent(status)}`;
    if (search.trim()) query += `&keywords=${encodeURIComponent(search.trim())}`;
    return query;
};

const formatDate = (value: string, format: string) => moment.utc(value).locale('fa').local().format(format);

const userDisplayName = (user: NonNullable<KuCoinHistory['transaction']>['createdBy']) =>
    user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName || '-';

/** هش و آدرس طولانی‌اند؛ کوتاه نمایش داده می‌شوند و مقدار کامل در تولتیپ و کپی می‌ماند. */
const LongValueCell: React.FC<{ value: string | null; label: string }> = ({ value, label }) =>
    value ? (
        <Tooltip title={<span className="en">{value}</span>} overlayClassName="pmx-kucoin-history-tooltip">
            <span className="long-value-cell">
                <CopyableText value={value} label={label} />
            </span>
        </Tooltip>
    ) : (
        <span className="en">-</span>
    );

export const KuCoinHistoryPage: React.FC = () => {
    const [kuCoinHistory, setKuCoinHistory] = React.useState<PaginationList<KuCoinHistory>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [historyType, setHistoryType] = React.useState<KuCoinHistoryType>(KuCoinHistoryType.Deposit);
    const [status, setStatus] = React.useState<string>('');
    const requestId = React.useRef(0);
    const [search, setSearch] = React.useState<string>('');
    const [appliedSearch, setAppliedSearch] = React.useState<string>('');
    const [sortOrder, setSortOrder] = React.useState<SortOrder>('descend');
    const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);
    const [selectedTransaction, setSelectedTransaction] = React.useState<KuCoinHistory['transaction']>(null);

    const isWithdrawal = historyType === KuCoinHistoryType.Withdrawal;
    const statusItems = isWithdrawal ? WITHDRAWAL_STATUS_ITEMS : DEPOSIT_STATUS_ITEMS;
    const currentPage = kuCoinHistory?.pagination?.pageIndex ?? 1;

    // هر تغییری در فیلترها از صفحه اول شروع می‌شود؛ صفحه‌ی فعلی ممکن است دیگر وجود نداشته باشد.
    const load = (
        page: number,
        size: number,
        options?: { type?: KuCoinHistoryType; search?: string; sort?: SortOrder; status?: string },
    ) => {
        const query = buildQuery(
            options?.search ?? appliedSearch,
            options?.type ?? historyType,
            options?.sort ?? sortOrder,
            options?.status ?? status,
        );
        const id = ++requestId.current;
        getAllKuCoinHistory(
            (value: boolean) => { if (id === requestId.current) setLoading(value); },
            (value: PaginationList<KuCoinHistory>) => { if (id === requestId.current) setKuCoinHistory(value); },
            page, size, query,
        );
    };

    // فقط بار اول؛ بقیه‌ی درخواست‌ها را خود فیلترها و جدول می‌فرستند.
    React.useEffect(() => {
        load(1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applySearch = () => {
        setAppliedSearch(search);
        load(1, pageSize, { search });
    };

    const clearSearch = () => {
        setSearch('');
        setAppliedSearch('');
        setStatus('');
        load(1, pageSize, { search: '', status: '' });
    };

    const changeHistoryType = (value: number | string) => {
        const type = Number(value) as KuCoinHistoryType;
        setHistoryType(type);
        setStatus('');
        load(1, pageSize, { type, status: '' });
    };

    const changeStatus = (value: number | string) => {
        const nextStatus = String(value);
        setStatus(nextStatus);
        load(1, pageSize, { status: nextStatus });
    };

    const handleTableChange = (
        pagination: TablePaginationConfig,
        _filters: unknown,
        sorter: SorterResult<KuCoinHistory> | SorterResult<KuCoinHistory>[],
    ) => {
        const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        const nextSort: SortOrder = activeSorter?.order === 'ascend' ? 'ascend' : 'descend';
        const nextSize = pagination.pageSize ?? pageSize;
        const sortChanged = nextSort !== sortOrder;
        const sizeChanged = nextSize !== pageSize;
        const nextPage = sortChanged || sizeChanged ? 1 : (pagination.current ?? 1);

        setPageSize(nextSize);
        setSortOrder(nextSort);
        load(nextPage, nextSize, { sort: nextSort });
    };

    const columns: ColumnsType<KuCoinHistory> = [
        {
            title: '#',
            key: 'index',
            width: 60,
            render: (_, __, index) => <span className="en">{(currentPage - 1) * pageSize + index + 1}</span>,
        },
        {
            title: 'ارز',
            dataIndex: 'asset',
            key: 'asset',
            width: 120,
            render: (asset: string) => (
                <span className="asset-cell">
                    <img src={getIconPath(asset)} alt="" />
                    <span className="en">{asset}</span>
                </span>
            ),
        },
        {
            title: 'مقدار',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 140,
            render: (quantity: number) => <span className="en">{digitSperator(quantity)}</span>,
        },
        ...(isWithdrawal
            ? ([
                  {
                      title: 'کارمزد',
                      dataIndex: 'fee',
                      key: 'fee',
                      width: 110,
                      render: (fee: number) => <span className="en">{digitSperator(fee)}</span>,
                  },
              ] as ColumnsType<KuCoinHistory>)
            : []),
        {
            title: 'کاربر',
            key: 'user',
            width: 180,
            render: (_, row) => {
                const createdBy = row.transaction?.createdBy;
                if (!createdBy) return <span className="muted">-</span>;
                return (
                    <div className="user-cell">
                        <UserLink userId={createdBy.id}>{userDisplayName(createdBy)}</UserLink>
                        {createdBy.userName && <small className="en">{createdBy.userName}</small>}
                    </div>
                );
            },
        },
        {
            title: 'کد پیگیری',
            key: 'trackingCode',
            width: 170,
            render: (_, row) => <CopyableText value={row.transaction?.trackingCode} label="کد پیگیری" />,
        },
        {
            title: 'تاریخ و ساعت',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 160,
            sorter: true,
            sortDirections: ['descend', 'ascend', 'descend'],
            sortOrder,
            render: (createTime: string) =>
                createTime ? (
                    <div className="date-cell">
                        <span>{formatDate(createTime, 'jYYYY/jMM/jDD')}</span>
                        <small>{formatDate(createTime, 'HH:mm')}</small>
                    </div>
                ) : (
                    '-'
                ),
        },
        ...(isWithdrawal
            ? ([
                  {
                      title: 'آدرس مقصد',
                      dataIndex: 'address',
                      key: 'address',
                      width: 220,
                      render: (address: string | null) => <LongValueCell value={address} label="آدرس" />,
                  },
              ] as ColumnsType<KuCoinHistory>)
            : []),
        {
            title: 'وضعیت کوکوین',
            dataIndex: 'status',
            key: 'status',
            width: 170,
            render: (value: string) => (
                <Tag color={value === 'Success' ? 'green' : value === 'Failure' ? 'red' : 'orange'}>
                    {statusItems.find((item) => item.id === value)?.title || value || '-'}
                </Tag>
            ),
        },
        {
            title: 'شبکه',
            dataIndex: 'network',
            key: 'network',
            width: 110,
            render: (network: string) =>
                network ? (
                    <Tag color="blue" className="en">
                        {network.toUpperCase()}
                    </Tag>
                ) : (
                    <span className="en">-</span>
                ),
        },
        {
            title: 'هش تراکنش',
            dataIndex: 'txid',
            key: 'txid',
            width: 240,
            render: (txid: string | null) => <LongValueCell value={txid} label="هش" />,
        },
        {
            title: 'وضعیت در PMX',
            key: 'transaction',
            width: 150,
            render: (_, row) =>
                row.transaction ? (
                    <div className="related-cell">
                        <Tag color="green">{isWithdrawal ? 'ثبت شده' : 'شارژ شده'}</Tag>
                        <Tooltip title="جزئیات تراکنش">
                            <button
                                type="button"
                                className="icon-button"
                                aria-label="جزئیات تراکنش"
                                onClick={() => setSelectedTransaction(row.transaction)}
                            >
                                <InfoCircleOutlined />
                            </button>
                        </Tooltip>
                    </div>
                ) : (
                    <Tag color="orange">{isWithdrawal ? 'ثبت نشده' : 'شارژ نشده'}</Tag>
                ),
        },
        ...(isWithdrawal
            ? ([{
                key: 'failureReason',
                width: 56,
                align: 'center',
                render: (_, row) => row.failureReason?.trim() ? (
                    <Popover
                        title="دلیل ناموفق بودن"
                        content={<div className="pmx-kucoin-failure-reason" dir="auto">{row.failureReason}</div>}
                        trigger="click"
                    >
                        <button type="button" className="icon-button" aria-label="دلیل ناموفق بودن">
                            <InfoCircleOutlined />
                        </button>
                    </Popover>
                ) : null,
            }] as ColumnsType<KuCoinHistory>)
            : []),
    ];

    return (
        <Page className="pmx-kucoin-history" title="سوابق واریز و برداشت کوکوین">
            <Card title="سوابق واریز و برداشت کوکوین">
                <div className="filter-row">
                    <div className="fields">
                        <Dropdown
                            items={HISTORY_TYPE_ITEMS}
                            onChangeHandle={changeHistoryType}
                            selectedOption={HISTORY_TYPE_ITEMS.find((i) => i.id === historyType)}
                        />
                        <Dropdown
                            items={statusItems}
                            onChangeHandle={changeStatus}
                            selectedOption={statusItems.find((item) => item.id === status)}
                        />
                        <Input
                            value={search}
                            className="search"
                            placeholder="نماد ارز یا هش تراکنش ..."
                            onPressEnter={applySearch}
                            name="search"
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="buttons">
                        <Button text="اعمال فیلتر" type={buttonType.success} disabled={loading} onClick={applySearch} />
                        <Button
                            text="حذف فیلتر"
                            type={buttonType.error}
                            disabled={loading || (!search && !appliedSearch && !status)}
                            onClick={clearSearch}
                        />
                        <Button
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            disabled={!kuCoinHistory?.items?.length}
                            onClick={() =>
                                downloadCSV(
                                    isWithdrawal ? 'سوابق برداشت کوکوین' : 'سوابق واریز کوکوین',
                                    kuCoinHistory?.items ?? [],
                                )
                            }
                        />
                    </div>
                </div>
                <AntdTable<KuCoinHistory>
                    className="kucoin-history-table"
                    rowKey={(row) => row.id || `${row.txid ?? ''}-${row.asset}-${row.createTime}`}
                    columns={columns}
                    dataSource={kuCoinHistory?.items ?? []}
                    loading={loading}
                    rowClassName={(row) => (row.transaction ? '' : 'not-linked-row')}
                    locale={{
                        emptyText: <Empty description={isWithdrawal ? 'برداشتی یافت نشد' : 'واریزی یافت نشد'} />,
                    }}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: kuCoinHistory?.pagination?.totalCount ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100, 500],
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                    onChange={handleTableChange}
                />
                <Modal
                    open={!!selectedTransaction}
                    onCancel={() => setSelectedTransaction(null)}
                    footer={null}
                    title="جزئیات تراکنش"
                    destroyOnClose
                >
                    {selectedTransaction && (
                        <Row gutter={[16, 16]} className="kucoin-transaction-details">
                            <Col span={12}>
                                <Typography.Text type="secondary">کد پیگیری</Typography.Text>
                                <div>
                                    <CopyableText value={selectedTransaction.trackingCode} label="کد پیگیری" />
                                </div>
                            </Col>
                            <Col span={12}>
                                <Typography.Text type="secondary">مبلغ</Typography.Text>
                                <div className="en">{digitSperator(selectedTransaction.amount)}</div>
                            </Col>
                            {selectedTransaction.createdBy && (
                                <Col span={12}>
                                    <Typography.Text type="secondary">ایجاد کننده</Typography.Text>
                                    <div>
                                        <UserLink userId={selectedTransaction.createdBy.id}>
                                            {userDisplayName(selectedTransaction.createdBy)}
                                        </UserLink>
                                    </div>
                                </Col>
                            )}
                            {selectedTransaction.createdOn && (
                                <Col span={12}>
                                    <Typography.Text type="secondary">تاریخ ایجاد</Typography.Text>
                                    <div>{formatDate(selectedTransaction.createdOn, 'jYYYY/jMM/jDD - HH:mm')}</div>
                                </Col>
                            )}
                            {(selectedTransaction.network?.name || selectedTransaction.network?.network) && (
                                <Col span={12}>
                                    <Typography.Text type="secondary">شبکه</Typography.Text>
                                    <div className="en">
                                        {selectedTransaction.network.name || selectedTransaction.network.network}
                                    </div>
                                </Col>
                            )}
                            {selectedTransaction.network?.fee != null && (
                                <Col span={12}>
                                    <Typography.Text type="secondary">کارمزد شبکه</Typography.Text>
                                    <div className="en">{digitSperator(selectedTransaction.network.fee)}</div>
                                </Col>
                            )}
                            {selectedTransaction.txId && (
                                <Col span={24}>
                                    <Typography.Text type="secondary">هش تراکنش</Typography.Text>
                                    <div className="full-hash">
                                        <CopyableText value={selectedTransaction.txId} label="هش" />
                                    </div>
                                </Col>
                            )}
                            {selectedTransaction.description && (
                                <Col span={24}>
                                    <Typography.Text type="secondary">توضیحات</Typography.Text>
                                    <div>{selectedTransaction.description}</div>
                                </Col>
                            )}
                        </Row>
                    )}
                </Modal>
            </Card>
        </Page>
    );
};
